import { Data, Option, Schema } from 'effect';

import type { CapabilityErrorUnion, CapabilityWriteErrorUnion } from '../capabilityError';
import { makeWriteCapabilityErrors } from '../capabilityError';

/**
 * What `Cart` reports when an operation does not answer.
 *
 * Two unions from one family, because reading a cart and changing one fail
 * differently. {@link CartError} is the three every capability has:
 * `NotFound` for a cart that is not there, `ContractMismatch` for a body the
 * curated schema rejects, `Unavailable` for everything else.
 * {@link CartWriteError} adds `Rejected` — the shop refused what the shopper
 * asked for, and said why.
 *
 * A page that only shows a cart handles three arms. A page with a quantity
 * stepper handles four, and the fourth is the one it renders next to the
 * stepper rather than in place of the page.
 */
export const CartError = makeWriteCapabilityErrors('Cart');

export type CartError = CapabilityErrorUnion<typeof CartError>;

export type CartWriteError = CapabilityWriteErrorUnion<typeof CartError>;

/**
 * What a cart read reports when the answer is not "there is no such cart".
 *
 * A caller that treats a missing cart as an empty one still has to report the
 * two failures that are not an answer at all, and this is that remainder. It is
 * named here rather than at each caller so `currentCart` and the token
 * lifecycle cannot disagree about which errors survive the `NotFound` fold.
 */
export type CartUnreadable = Exclude<CartError, InstanceType<typeof CartError.NotFound>>;

/**
 * The backend accepted a write, and the read that shows the resulting cart
 * failed. The write committed, so the cart state is unknown, not refused.
 */
export class CartReadAfterWriteError extends Data.TaggedError('@engine/Cart/ReadAfterWrite')<{
  readonly cause: unknown;
}> {}

/**
 * A failed read after a successful write does not prove that the write failed.
 * In particular, a 404 from that read must not trigger another non-idempotent add.
 */
export const cartConfirmationFailure = (operation: string, token: string, cause: unknown) =>
  Schema.isSchemaError(cause)
    ? new CartError.ContractMismatch({ operation, reference: Option.some(token), cause })
    : new CartError.Unavailable({
        operation,
        reference: Option.some(token),
        cause: new CartReadAfterWriteError({ cause }),
      });
